"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { FeaturedCarousel } from "@/components/sections/featured-carousel/featured-carousel";
import { FilterBar } from "@/components/sections/filter-bar";
import { ProductCardPH } from "@/components/products/product-card-ph";
import { ProductCardSkeleton } from "@/components/products/product-card-skeleton";
import { WeekProductsWidget } from "@/components/sections/sidebar/week-products";
import { DiscussionsWidget } from "@/components/sections/sidebar/discussions";
import { NewsletterWidget } from "@/components/sections/sidebar/newsletter";
import {
  listProductsGroupedByWeek,
  mapProductSummaryToCardProduct,
  toggleProductVote,
  type WeekWithProducts,
  type ProductWeekResponse,
  type WeekStats
} from "@/lib/services/productService";
import { useAuth } from "@/lib/auth/auth-context";

// Format week date range in Russian
function formatWeekRange(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const dayFormatter = new Intl.DateTimeFormat('ru-RU', { day: 'numeric' });
  const dayMonthFormatter = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });

  const startDay = dayFormatter.format(start);
  const endFormatted = dayMonthFormatter.format(end);

  // Same month: "5-11 янв."
  if (start.getMonth() === end.getMonth()) {
    return `${startDay}-${endFormatted}`;
  }

  // Different months: "28 дек. - 3 янв."
  return `${dayMonthFormatter.format(start)} - ${endFormatted}`;
}

// Check if a date falls within the current week
function isCurrentWeek(weekStartDate: string): boolean {
  const today = new Date();
  const weekStart = new Date(weekStartDate);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 7);
  return today >= weekStart && today < weekEnd;
}

// Get Monday of the current week
function getCurrentMonday(): Date {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() + diff);
  return monday;
}

// Generate 3 consecutive weeks: current, previous, 2 weeks ago
function getWeekFilters(): { label: string; startDate: Date; endDate: Date }[] {
  const labels = ["Эта неделя", "Прошлая неделя", "2 недели назад"];
  const monday = getCurrentMonday();

  return labels.map((label, i) => {
    const start = new Date(monday);
    start.setDate(start.getDate() - i * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { label, startDate: start, endDate: end };
  });
}

// Calculate days remaining in the week
function getDaysRemaining(weekStartDate: string): number {
  const weekStart = new Date(weekStartDate);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 7);
  const now = new Date();
  const diff = weekEnd.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

// Russian pluralization for days
function pluralizeDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'дня';
  return 'дней';
}

// Week header component
function WeekHeader({ week, stats, isCurrent }: {
  week: ProductWeekResponse;
  stats: WeekStats;
  isCurrent: boolean;
}) {
  const dateRange = formatWeekRange(week.weekStartDate, week.weekEndDate);

  if (isCurrent) {
    const daysLeft = getDaysRemaining(week.weekStartDate);

    return (
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex rounded-full h-1.5 w-1.5 bg-success" />
            <h2 className="text-base font-semibold text-foreground">Эта неделя</h2>
            {week.name && (
              <span className="text-sm text-muted-foreground">· {week.name}</span>
            )}
          </div>
          <span className="text-xs text-muted-foreground tabular-nums">
            {daysLeft} {pluralizeDays(daysLeft)} осталось
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
          <span>{dateRange}</span>
          <span className="text-muted-foreground/40">·</span>
          <span>{stats.totalProducts} продуктов</span>
          <span className="text-muted-foreground/40">·</span>
          <span>{stats.totalVotes.toLocaleString('ru-RU')} голосов</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 pt-10 pb-4">
      <div className="h-px flex-1 bg-border" />
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
        <span className="font-medium">{dateRange}</span>
        {week.name && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <span>{week.name}</span>
          </>
        )}
        <span className="text-muted-foreground/40">·</span>
        <span>{stats.totalProducts} продуктов</span>
        <span className="text-muted-foreground/40">·</span>
        <span>{stats.totalVotes.toLocaleString('ru-RU')} голосов</span>
      </div>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export default function HomePage() {
  const [filterType, setFilterType] = useState<"popular" | "new" | "following" | "week">("popular");
  const [weeks, setWeeks] = useState<WeekWithProducts[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const oldestWeekIdRef = useRef<number | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const searchParams = useSearchParams();
  const { isAuthenticated } = useAuth();

  // Compute 3 consecutive week filters from current date
  const weekFilters = useMemo(() => getWeekFilters(), []);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);

  // Format date as YYYY-MM-DD for API
  const formatDateParam = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Fetch initial products grouped by week
  const fetchInitial = useCallback(async () => {
    setIsLoading(true);
    oldestWeekIdRef.current = null;
    setHasMore(true);

    const sort = filterType === 'new' ? 'new' : filterType === 'following' ? 'voted' : 'popular';
    const weekStart = filterType === 'week' ? formatDateParam(weekFilters[selectedWeekIndex].startDate) : undefined;
    const { success, data } = await listProductsGroupedByWeek({ sort, weekStart });

    if (success && data) {
      setWeeks(data.weeks);
      setHasMore(filterType !== 'week' && data.weeks.length > 0);

      if (data.weeks.length > 0) {
        const lastWeek = data.weeks[data.weeks.length - 1];
        oldestWeekIdRef.current = lastWeek.week.id;
      }
    }
    setIsLoading(false);
  }, [filterType, selectedWeekIndex, weekFilters]);

  // Load more weeks (infinite scroll)
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !oldestWeekIdRef.current) return;

    setIsLoadingMore(true);
    const sort = filterType === 'new' ? 'new' : filterType === 'following' ? 'voted' : 'popular';

    const { success, data } = await listProductsGroupedByWeek({
      beforeWeekId: oldestWeekIdRef.current,
      sort,
    });

    if (success && data) {
      if (data.weeks.length === 0) {
        setHasMore(false);
      } else {
        setWeeks(prev => [...prev, ...data.weeks]);
        const lastWeek = data.weeks[data.weeks.length - 1];
        oldestWeekIdRef.current = lastWeek.week.id;
      }
    }
    setIsLoadingMore(false);
  }, [isLoadingMore, hasMore, filterType]);

  // Initial fetch on mount and filter change
  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    if (!trigger || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore && hasMore) {
          loadMore();
        }
      },
      { rootMargin: '200px', threshold: 0 }
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [loadMore, isLoadingMore, hasMore]);

  // Check for unauthorized error from proxy redirect
  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'unauthorized') {
      toast.error('Доступ запрещён', {
        description: 'Для входа в админ-панель нужны права администратора',
        duration: 5000,
      });

      if (window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.delete('error');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, [searchParams]);

  // Vote handler - updates product within nested weeks structure
  const handleVote = async (productSlug: string) => {
    if (!isAuthenticated) {
      toast.error("Войдите, чтобы голосовать");
      return;
    }

    try {
      const result = await toggleProductVote(productSlug);

      if (!result.success) {
        throw new Error(result.error?.message || "Не удалось проголосовать");
      }

      if (result.data) {
        setWeeks(prevWeeks =>
          prevWeeks.map(week => ({
            ...week,
            products: week.products.map(product =>
              product.slug === productSlug
                ? { ...product, voteCount: result.data!.voteCount, isVoted: result.data!.isVoted }
                : product
            ),
          }))
        );
      }
    } catch (error) {
      console.error("Vote error:", error);
      throw error;
    }
  };

  const weekOptions = useMemo(() =>
    weekFilters.map(w => ({
      label: w.label,
      dateRange: formatWeekRange(w.startDate.toISOString(), w.endDate.toISOString()),
    })),
  [weekFilters]);

  // Get week label for filter bar
  const weekLabel = weekOptions.length > 0
    ? weekOptions[0].dateRange
    : "";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <FeaturedCarousel />
      {/*<DiscountsEventsCarousel />*/}
      <FilterBar
        currentFilter={filterType}
        onFilterChange={setFilterType}
        weekLabel={weekLabel}
        weekOptions={weekOptions}
        selectedWeekIndex={selectedWeekIndex}
        onWeekSelect={setSelectedWeekIndex}
      />

      <main className="flex-1">
        <div className="container max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main Content - 70% */}
            <div className="lg:col-span-8">
              <h1 className="text-2xl font-bold text-foreground mb-4">Продукты недели</h1>

              {/* Product List */}
              {isLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <ProductCardSkeleton key={i} />
                  ))}
                </div>
              ) : weeks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="text-5xl mb-4">👀</div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">А где?</h3>
                  <p className="text-muted-foreground max-w-sm">
                    Пока их нет, но скоро будут. Следите за обновлениями!
                  </p>
                </div>
              ) : (
                <>
                  {/* Weeks with Products */}
                  {weeks.map((weekData) => (
                    <div key={weekData.week.id} className="mb-4">
                      <WeekHeader
                        week={weekData.week}
                        stats={weekData.stats}
                        isCurrent={isCurrentWeek(weekData.week.weekStartDate)}
                      />

                      <div className="space-y-4">
                        {weekData.products.map((product, productIndex) => (
                          <ProductCardPH
                            key={product.id}
                            product={mapProductSummaryToCardProduct(product)}
                            rank={productIndex + 1}
                            isAuthenticated={isAuthenticated}
                            enableHoverPreview={true}
                            onVote={handleVote}
                          />
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Infinite Scroll Trigger */}
                  <div ref={loadMoreTriggerRef} className="flex justify-center py-8">
                    {isLoadingMore ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="text-sm">Загрузка...</span>
                      </div>
                    ) : !hasMore ? (
                      <p className="text-sm text-muted-foreground">
                        Вы просмотрели все продукты
                      </p>
                    ) : null}
                  </div>
                </>
              )}
            </div>

            {/* Sidebar - 30% */}
            <aside className="lg:col-span-4">
              <div className="space-y-6 sticky top-24">
                <WeekProductsWidget />
                <DiscussionsWidget />
                <NewsletterWidget />
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
